import { useEffect, useRef, useState } from "react";
import { ArrowDownIcon, ArrowUpIcon, ListChecksIcon } from "lucide-react";
import type { FindingPdfEdit } from "@repo/shared/findingChanges";
import { Button } from "../../../ui/button";
import { Select, SelectTrigger, SelectPopup, SelectItem } from "../../../ui/select";
import { activeChangeIndex } from "./changeNavigation";

export function FindingChangesNavigation({
  edits,
  sectionIdPrefix,
  checksCount,
}: {
  edits: ReadonlyArray<FindingPdfEdit>;
  sectionIdPrefix: string;
  checksCount: number;
}) {
  const root = useRef<HTMLElement>(null);
  const [active, setActive] = useState(0);
  useEffect(() => {
    const nav = root.current;
    if (!nav) return;
    let scroller: HTMLElement | null = nav.parentElement;
    while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) {
      scroller = scroller.parentElement;
    }
    const scrollRoot = scroller ?? document.documentElement;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const tops = edits.map(
        (_, index) =>
          document.getElementById(`${sectionIdPrefix}-${index}`)?.getBoundingClientRect().top ??
          Infinity,
      );
      const atEnd =
        scrollRoot.scrollTop > 0 &&
        scrollRoot.scrollHeight - scrollRoot.clientHeight - scrollRoot.scrollTop <= 2;
      setActive(activeChangeIndex(tops, scrollRoot.getBoundingClientRect().top + 26, atEnd));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    // Capture scrolling in either the page or the resolution dialog.
    window.addEventListener("scroll", schedule, true);
    const observer = new ResizeObserver(schedule);
    observer.observe(nav.parentElement!.parentElement!);
    observer.observe(nav.parentElement!);
    measure();
    return () => {
      window.removeEventListener("scroll", schedule, true);
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [edits, sectionIdPrefix]);
  const jump = (suffix: number | "checks") => {
    const target = document.getElementById(`${sectionIdPrefix}-${suffix}`);
    if (!target) return;
    target.scrollIntoView({ block: "start", behavior: "instant" });
    target.focus({ preventScroll: true });
  };
  if (edits.length < 2) return null;
  const index = Math.min(active, edits.length - 1);
  return (
    <nav
      ref={root}
      aria-label="Navigate proposed changes"
      className="flex min-w-0 items-center gap-1"
    >
      <Select
        value={String(index)}
        onValueChange={(value) => {
          if (value !== null) jump(Number(value));
        }}
      >
        <SelectTrigger
          size="sm"
          variant="ghost"
          className="w-20 min-w-0 sm:w-52"
          aria-label="Jump to change"
        >
          <span className="truncate text-xs">
            <span className="mr-2 tabular-nums text-muted-foreground">
              {index + 1} / {edits.length}
            </span>
            <span className="hidden sm:inline">{edits[index]?.section}</span>
          </span>
        </SelectTrigger>
        <SelectPopup alignItemWithTrigger={false}>
          {edits.map((edit, i) => (
            <SelectItem key={i} value={String(i)}>
              <span className="text-xs">
                {i + 1}. {edit.section}
                <span className="ml-2 text-muted-foreground">Page {edit.beforePage}</span>
              </span>
            </SelectItem>
          ))}
        </SelectPopup>
      </Select>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          size="sm"
          variant="ghost"
          className="cursor-pointer"
          aria-label="Previous change"
          disabled={index === 0}
          onClick={() => jump(index - 1)}
        >
          <ArrowUpIcon className="size-4" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="cursor-pointer"
          aria-label="Next change"
          disabled={index === edits.length - 1}
          onClick={() => jump(index + 1)}
        >
          <ArrowDownIcon className="size-4" />
        </Button>
        {checksCount > 0 ? (
          <Button
            size="sm"
            variant="ghost"
            className="cursor-pointer text-xs"
            onClick={() => jump("checks")}
            aria-label={`Before approval: ${checksCount} checks`}
          >
            <ListChecksIcon className="size-4" />
            <span className="hidden sm:inline">Checks</span>
            <span className="tabular-nums">{checksCount}</span>
          </Button>
        ) : null}
      </div>
    </nav>
  );
}
